/**
 * Duplicate Payment Analysis (FSD 2.2.2.10 "suspected and prevented duplicates with the
 * detection reason"; FT10-U16).
 *
 * A thin wrapper over the generated `apReportsDuplicatePayments`. Only the filters that
 * are set travel; the server supplies its own defaults (a look-back ending today in the
 * tenant's timezone, a 7-day window) and states them back on the report.
 */

import {
  apReportsDuplicatePayments,
  type ApDuplicatePaymentsDTO,
  type ApDuplicatePaymentsQuery,
} from '@erpx/api'

import { api } from '@/api/client'

export type { ApDuplicatePaymentsDTO }

export const DUPLICATE_PAYMENTS_PERMISSIONS = {
  reportRead: 'ap.report.read',
  companyRead: 'org.company.read',
} as const

/** `ApDuplicatePaymentsQuery.windowDays` is validated `min=1,max=60` server-side. */
export const WINDOW_MIN = 1
export const WINDOW_MAX = 60

export interface DuplicatePaymentsFilters {
  companyId: number
  from: string
  to: string
  windowDays: number | null
}

export const DUPLICATE_PAYMENTS_KEYS = {
  all: ['ap', 'reports', 'duplicate-payments'] as const,
  one: (filters: DuplicatePaymentsFilters) => ['ap', 'reports', 'duplicate-payments', filters] as const,
} as const

export function duplicatePaymentsQuery(filters: DuplicatePaymentsFilters): ApDuplicatePaymentsQuery {
  return {
    companyId: filters.companyId,
    ...(filters.from === '' ? {} : { from: filters.from }),
    ...(filters.to === '' ? {} : { to: filters.to }),
    ...(filters.windowDays === null ? {} : { windowDays: filters.windowDays }),
  }
}

export function duplicatePayments(filters: DuplicatePaymentsFilters): Promise<ApDuplicatePaymentsDTO> {
  return apReportsDuplicatePayments(api(), duplicatePaymentsQuery(filters))
}
